// Enums
import { ContractStatesEnumeration } from 'containers/contract/enumerations/contract-states.enumeration';
import { ContractConsentsTypeEnum } from 'containers/contract/types/contract-api-data-api-response.type';
// Resolvers
import {
  generateOtpIdResolver,
  loadScreenDetailsResolver,
  saveScreenResolver,
} from '../resolvers/contract.view.resolver';
// Translations
import { CONTINUE_CONTRACT } from './translations/contract.view.handlers.translations';
// Types
import {
  CheckConsentHandlerType,
  ContinueToOtpClickHandlerType,
  ContractViewHandlerType,
  ContractViewHandlerTypeBuilder,
  LoadScreenDetailsHandlerType,
  SetupContractButtonConfigurationHandlerType,
} from './types/contract.view.handlers.types';
import { PreContractViewDataApiResponseType } from 'containers/contract/views/pre-contractual/types/pre-contract-view-data-api-response.type';
// Utils
import {
  checkIfAllConsentsAreCorrect,
  isConsentChecked,
  updateButtonWithDisabled,
} from './utils/contract.view.handlers.utils';
import { downloadFileAsBlob } from '@openbank/cf-ui-framework';

const continueToOtpClickHandler = async ({
  setMainButtonConfiguration,
  setContractState,
  setOtpId,
  setError,
  consents,
  handleContinueButtonClickTracking,
}: ContinueToOtpClickHandlerType): Promise<void> => {
  if (consents) {
    setMainButtonConfiguration(updateButtonWithDisabled(true));
    handleContinueButtonClickTracking();
    const [, error] = await saveScreenResolver(consents);
    if (error) {
      setError(true);
    } else {
      const [otpResponse, otpError] = await generateOtpIdResolver();
      if (otpError) {
        setError(true);
      } else {
        setOtpId(otpResponse?.otpId ?? '');
        setContractState(ContractStatesEnumeration.OTP);
      }
    }
  }
};

const saveByteCodeFromPrecontractual = ({
  response,
  setScreenDetails,
}: {
  response: PreContractViewDataApiResponseType;
  setScreenDetails: LoadScreenDetailsHandlerType['setScreenDetails'];
}) => {
  const { url, ...restDocument } = response.document;

  downloadFileAsBlob({
    url,
    callback: (byteCode: Blob): void => {
      setScreenDetails({
        document: { ...restDocument, url, byteCode },
        consents: response.consents,
      });
    },
  });
};

const loadScreenDetailsHandler = async ({
  setScreenDetails,
  setConsents,
  setError,
}: LoadScreenDetailsHandlerType): Promise<void> => {
  const [response, error] = await loadScreenDetailsResolver();
  if (error) {
    setError(true);
  } else if (response) {
    saveByteCodeFromPrecontractual({ response, setScreenDetails });
    setConsents(
      response?.consents.map(consent => ({
        type: consent.type,
        checked: false,
        mandatory: consent.mandatory,
      }))
    );
  }
};

const setupContractButtonConfigurationHandler = ({
  formatMessage,
  setMainButtonConfiguration,
  setContractState,
  setOtpId,
  consents,
  setError,
  handleContinueButtonClickTracking,
}: SetupContractButtonConfigurationHandlerType): void => {
  if (consents) {
    setMainButtonConfiguration({
      children: formatMessage({ id: CONTINUE_CONTRACT }),
      disabled: !checkIfAllConsentsAreCorrect(consents),
      type: 'PRIMARY',
      size: 'LARGE',
      onClick: () =>
        continueToOtpClickHandler({
          consents,
          setContractState,
          setMainButtonConfiguration,
          setOtpId,
          setError,
          handleContinueButtonClickTracking,
        }),
    });
  }
};

const checkConsentHandler = ({
  consent,
  consents,
  setConsents,
  setMainButtonConfiguration,
  handleConsentsCheckboxChangeTracking,
}: CheckConsentHandlerType) => {
  if (consents && setConsents) {
    const updatedConsents = consents.map(consentIteration =>
      consentIteration.type === consent
        ? { ...consentIteration, checked: !consentIteration.checked }
        : consentIteration
    );
    handleConsentsCheckboxChangeTracking({
      checked: isConsentChecked({ updatedConsents, consent }),
      type: consent,
    });
    setConsents(updatedConsents);
    setMainButtonConfiguration(updateButtonWithDisabled(checkIfAllConsentsAreCorrect(consents)));
  }
};

const ContractViewHandlers = ({
  formatMessage,
  setMainButtonConfiguration,
  setConsents,
  setScreenDetails,
  setContractState,
  setOtpId,
  setError,
  consents,
  handleContinueButtonClickTracking,
  handleConsentsCheckboxChangeTracking,
}: ContractViewHandlerTypeBuilder): ContractViewHandlerType => ({
  handleSetupContractButtonConfiguration: () =>
    setupContractButtonConfigurationHandler({
      consents,
      formatMessage,
      setMainButtonConfiguration,
      setContractState,
      setOtpId,
      setError,
      handleContinueButtonClickTracking,
    }),
  handleLoadScreenDetails: () =>
    loadScreenDetailsHandler({
      setConsents,
      setScreenDetails,
      setError,
    }),
  handleCheckConsent: (consent: ContractConsentsTypeEnum) =>
    checkConsentHandler({
      setMainButtonConfiguration,
      setConsents,
      consent,
      consents,
      handleConsentsCheckboxChangeTracking,
    }),
});

export { ContractViewHandlers };
